/**
 * StatCard — THE canonical KPI tile (GUI makeover item 19).
 *
 * All stat/KPI tiles render through this component; do not hand-roll
 * `.stat-card` markup or local StatCard copies in routes. Mobile behaviour
 * (2-per-row on phones) is centralised via `StatGrid` + the `kpi-grid`
 * class in index.css — never per-page.
 */
import { TrendingUp, TrendingDown } from 'lucide-react'
import { clsx } from 'clsx'
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'

interface StatCardProps {
  label: string
  value: string | number
  change?: number            // signed number (positive = good)
  changeLabel?: string       // e.g. "today", "vs last month"
  /** Canonical alias for changeLabel (e.g. "30d", "YTD"). Wins if both set. */
  trendLabel?: string
  icon?: LucideIcon
  iconColor?: string         // tailwind text-* class
  /** Sub-line under the value/trend (context, breakdown, caveat). */
  sub?: string
  /** Legacy alias for `sub`. */
  footer?: string
  accentColor?: 'blue' | 'amber' | 'purple' | 'emerald' | 'rose'
  size?: 'compact' | 'default' | 'large'
  /** Override the value's text classes (e.g. tone colour, font-mono). */
  valueClassName?: string
  className?: string
}

const ACCENT = {
  blue:    'text-blue-light   bg-blue/10    border-blue/20',
  amber:   'text-amber-light  bg-amber/10   border-amber/20',
  purple:  'text-purple-light bg-purple/10  border-purple/20',
  emerald: 'text-emerald-light bg-emerald/10 border-emerald/20',
  rose:    'text-rose-light   bg-rose/10    border-rose/20',
}

// `.stat-value` (index.css) supplies the default 2xl weight/tracking; the
// compact/large sizes override it via utility classes (utilities layer wins).
const VALUE_SIZE = {
  compact: 'text-lg',
  default: '',
  large:   'text-3xl',
}

export default function StatCard({
  label, value, change, changeLabel = 'today', trendLabel,
  icon: Icon, iconColor, sub, footer, accentColor, size = 'default',
  valueClassName, className,
}: StatCardProps) {
  const positive = change !== undefined && change >= 0
  const periodLabel = trendLabel ?? changeLabel
  const changeText = change !== undefined
    ? `${positive ? '+' : ''}${change.toFixed(2)}% ${periodLabel}`
    : undefined
  const subText = sub ?? footer

  return (
    <div className={clsx('stat-card', size === 'compact' && '!p-4', className)}>
      <div className="flex items-start justify-between">
        <p className="stat-label">{label}</p>
        {Icon && (
          <div className={clsx('w-7 h-7 rounded-lg border flex items-center justify-center shrink-0',
            accentColor ? ACCENT[accentColor] : 'bg-surface-hover border-border'
          )}>
            <Icon size={14} aria-hidden className={iconColor ?? ''} />
          </div>
        )}
      </div>

      <p className={clsx('stat-value tabular', VALUE_SIZE[size], valueClassName)}>
        {value}
      </p>

      {changeText && (
        <div>
          <span className={positive ? 'delta-chip-pos' : 'delta-chip-neg'}>
            {positive
              ? <TrendingUp size={11} aria-hidden />
              : <TrendingDown size={11} aria-hidden />}
            <span aria-label={`${positive ? 'Up' : 'Down'} ${Math.abs(change!).toFixed(2)}% ${periodLabel}`}>
              {changeText}
            </span>
          </span>
        </div>
      )}

      {subText && <p className="text-xs text-text-muted">{subText}</p>}
    </div>
  )
}

// ── StatGrid ─────────────────────────────────────────────────────────────────
// Canonical KPI row wrapper. `kpi-grid` (index.css) forces 2-per-row on
// phones regardless of the desktop column count, so pages never re-implement
// the mobile breakpoint.

const GRID_COLS: Record<2 | 3 | 4 | 5 | 6, string> = {
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
  5: 'sm:grid-cols-2 lg:grid-cols-5',
  6: 'sm:grid-cols-3 lg:grid-cols-6',
}

export function StatGrid({
  cols = 4,
  className,
  children,
}: {
  cols?: 2 | 3 | 4 | 5 | 6
  className?: string
  children: ReactNode
}) {
  return (
    <div className={clsx('kpi-grid grid grid-cols-2 gap-4', GRID_COLS[cols], className)}>
      {children}
    </div>
  )
}
